import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { Mic } from "lucide-react";

/**
 * PostFormInputs - Title and content textarea inputs with voice-to-text
 * Handles text input and speech recognition for post content
 */
const PostFormInputs = ({
	content,
	setContent,
	textareaRef,
	isListening,
	onStartSpeechRecognition,
	onStopSpeechRecognition,
	selectedPostType,
	contestName,
	userName,
	isPosting,
	isRecording,
	isVideo,
	showPostPreview,
	currentLumens,
	placeholder,
	className = "",
}) => {
	const isDisabled = isPosting || isRecording || isVideo || showPostPreview;

	const isGratefulWithoutLumens =
		selectedPostType === "grateful" && (currentLumens || 0) < 2;

	// Don't show if grateful post without lumens
	if (isGratefulWithoutLumens) {
		return null;
	}

	const defaultPlaceholder =
		placeholder ||
		(contestName?.toLowerCase() !== "glow game" &&
		contestName?.toLowerCase() !== "the glow game"
			? "What did you do for this contest? Your submission will also be graded for glow"
			: selectedPostType === "grateful"
				? "Who are you grateful for?"
				: selectedPostType === "glow"
					? "What unexpected joy did you bring to others?"
					: `What's on your mind, ${userName?.split(" ")[0] || "there"}?`);

	const borderColorClass =
		selectedPostType === "grateful"
			? "border-green-500 dark:border-green-500"
			: selectedPostType === "pride"
				? "border-purple-500 dark:border-purple-500"
				: "border-yellow-500 dark:border-yellow-500";

	return (
		<div className={`relative ${className}`}>
			<Textarea
				ref={textareaRef}
				placeholder={defaultPlaceholder}
				className={`min-h-20 w-full resize-none border-2 bg-white/50 dark:bg-gray-800/50 ${borderColorClass} pr-12 text-foreground placeholder:text-gray-600 dark:text-foreground dark:placeholder:text-gray-400 ${
					showPostPreview
						? "disabled:text-foreground disabled:opacity-100 disabled:dark:text-foreground"
						: ""
				}`}
				value={content}
				onChange={(e) => setContent(e.target.value)}
				disabled={isDisabled}
			/>
			{/* Voice-to-text microphone button */}
			<button
				type="button"
				onClick={() => {
					if (isListening) {
						onStopSpeechRecognition();
					} else {
						onStartSpeechRecognition();
					}
				}}
				disabled={isDisabled}
				className={`absolute bottom-2 right-2 rounded-full border-2 border-red-500 p-2 transition-colors ${
					isListening
						? "animate-pulse border-red-600 bg-red-500 text-white hover:bg-red-600"
						: "border-red-500 bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
				} ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
				aria-label={isListening ? "Stop voice input" : "Start voice input"}
				title={isListening ? "Stop voice input" : "Start voice input"}
			>
				<Mic size={18} className={isListening ? "animate-pulse" : ""} />
			</button>
		</div>
	);
};

export default PostFormInputs;
